import { useState, useRef } from 'react'
import {
  CloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Input,
  Checkbox,
  Progress,
  Badge,
  Table,
  Accordion,
  Dialog,
} from '@chakra-ui/react';
import { FiUpload, FiCheckCircle, FiAlertTriangle } from 'react-icons/fi'
import { useImportEmployment } from '../../hooks/useBusiness'
import type { EmploymentImportResult } from '../../types/business'
import { ResponsiveModal } from '../shared'
import { toaster } from '@/components/ui/toaster'

interface BulkImportModalProps {
  isOpen: boolean
  onClose: () => void
}

export function BulkImportModal({ isOpen, onClose }: BulkImportModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null!)
  const importMutation = useImportEmployment()

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [updateExisting, setUpdateExisting] = useState(true)
  const [createWorksites, setCreateWorksites] = useState(true)
  const [createUnions, setCreateUnions] = useState(true)
  const [result, setResult] = useState<EmploymentImportResult | null>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toaster.create({ title: 'Only CSV files are supported', type: 'warning', duration: 3000 })
        return
      }
      setSelectedFile(file)
      setResult(null)
    }
  }

  const handleImport = async () => {
    if (!selectedFile) return

    try {
      const importResult = await importMutation.mutateAsync({
        file: selectedFile,
        options: {
          updateExisting,
          createMissingWorksites: createWorksites,
          createMissingUnions: createUnions,
        },
      })
      setResult(importResult)
      toaster.create({
        title: 'Import complete',
        description: `${importResult.employmentsCreated} created, ${importResult.employmentsUpdated} updated`,
        type: importResult.errorCount > 0 ? 'warning' : 'success',
        duration: 5000,
      })
    } catch {
      toaster.create({ title: 'Import failed', type: 'error', duration: 5000 })
    }
  }

  const handleClose = () => {
    setSelectedFile(null)
    setResult(null)
    onClose()
  }

  return (
    <ResponsiveModal isOpen={isOpen} onClose={handleClose} size="xl">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <Dialog.Header>Import Employment Data</Dialog.Header>
          <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
          <Dialog.Body>
            <VStack gap={4} align="stretch">
              {/* File selection */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>CSV File</Text>
                <HStack>
                  <Button
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    colorPalette="brand"
                    variant="outline"><FiUpload />Choose File
                                    </Button>
                  <Text fontSize="sm" color="gray.600">
                    {selectedFile ? selectedFile.name : 'No file selected'}
                  </Text>
                  {selectedFile && (
                    <Badge bg="info.subtle" color="info.fg" fontSize="xs">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </Badge>
                  )}
                </HStack>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  display="none"
                  onChange={handleFileChange}
                />
              </Box>

              {/* Options */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>Options</Text>
                <VStack align="start" gap={1}>
                  <Checkbox.Root
                    size="sm"
                    onCheckedChange={e => setUpdateExisting(e.checked === true)}
                    checked={updateExisting}
                  ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>Update existing employment records
                                      </Checkbox.Label></Checkbox.Root>
                  <Checkbox.Root
                    size="sm"
                    onCheckedChange={e => setCreateWorksites(e.checked === true)}
                    checked={createWorksites}
                  ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>Create missing worksites
                                      </Checkbox.Label></Checkbox.Root>
                  <Checkbox.Root
                    size="sm"
                    onCheckedChange={e => setCreateUnions(e.checked === true)}
                    checked={createUnions}
                  ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>Create missing unions
                                      </Checkbox.Label></Checkbox.Root>
                </VStack>
              </Box>

              {/* Expected columns */}
              <Box bg="gray.50" p={3} borderRadius="md">
                <Text fontSize="xs" fontWeight="medium" color="gray.600" mb={1}>
                  Expected CSV Columns
                </Text>
                <Text fontSize="xs" color="gray.500">
                  <strong>Contact:</strong> VoterFileId, FirstName, LastName, StreetAddress, City, State, Zip
                </Text>
                <Text fontSize="xs" color="gray.500">
                  <strong>Worksite:</strong> BusinessName, BusinessType, BusinessAddress, BusinessCity, BusinessState, BusinessZip
                </Text>
                <Text fontSize="xs" color="gray.500">
                  <strong>Employment:</strong> Position, Department, StartDate, IsActive
                </Text>
                <Text fontSize="xs" color="gray.500">
                  <strong>Union:</strong> UnionName, UnionLocalNumber, UnionMemberNumber
                </Text>
                <Text fontSize="xs" color="gray.400" mt={1}>
                  Column name variations are automatically recognized (e.g., employer_name, company, job_title).
                </Text>
              </Box>

              {/* Progress */}
              {importMutation.isPending && (
                <Box>
                  <Text fontSize="sm" color="gray.600" mb={1}>Importing...</Text>
                  <Progress.Root size="sm" value={null} colorPalette="brand" borderRadius="full">
                    <Progress.Track>
                      <Progress.Range />
                    </Progress.Track>
                  </Progress.Root>
                </Box>
              )}

              {/* Results */}
              {result && (
                <Box
                  bg={result.errorCount > 0 ? 'orange.50' : 'green.50'}
                  p={4}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={result.errorCount > 0 ? 'orange.200' : 'green.200'}
                >
                  <HStack mb={2}>
                    <Box as={result.errorCount > 0 ? FiAlertTriangle : FiCheckCircle}
                      color={result.errorCount > 0 ? 'orange.500' : 'green.500'}
                    />
                    <Text fontWeight="medium" fontSize="sm">
                      Import Complete
                    </Text>
                  </HStack>

                  <VStack align="start" gap={0} fontSize="sm">
                    <HStack gap={4}>
                      <Text>Total rows: <strong>{result.totalRows}</strong></Text>
                      <Text>Processed: <strong>{result.processedCount}</strong></Text>
                    </HStack>
                    <HStack gap={4}>
                      <Text>Employments created: <Badge colorPalette="green" fontSize="xs">{result.employmentsCreated}</Badge></Text>
                      <Text>Updated: <Badge bg="info.subtle" color="info.fg" fontSize="xs">{result.employmentsUpdated}</Badge></Text>
                    </HStack>
                    <HStack gap={4} flexWrap="wrap">
                      <Text>Worksites created: <Badge colorPalette="purple" fontSize="xs">{result.worksitesCreated}</Badge></Text>
                      {result.worksitesFuzzyMatched > 0 && (
                        <Text>Fuzzy matched: <Badge colorPalette="orange" fontSize="xs">{result.worksitesFuzzyMatched}</Badge></Text>
                      )}
                      <Text>Unions created: <Badge colorPalette="teal" fontSize="xs">{result.unionsCreated}</Badge></Text>
                    </HStack>
                    {result.skippedCount > 0 && (
                      <Text>Skipped: <Badge colorPalette="gray" fontSize="xs">{result.skippedCount}</Badge></Text>
                    )}
                  </VStack>

                  {/* Fuzzy match details */}
                  {result.fuzzyMatches && result.fuzzyMatches.length > 0 && (
                    <Accordion.Root collapsible mt={3}>
                      <Accordion.Item border="none" value='item-0'>
                        <Accordion.ItemTrigger px={0} py={1}>
                          <Text fontSize="sm" color="orange.600" flex="1" textAlign="left">
                            {result.fuzzyMatches.length} worksite{result.fuzzyMatches.length !== 1 ? 's' : ''} matched by similarity
                          </Text>
                          <Accordion.ItemIndicator />
                        </Accordion.ItemTrigger>
                        <Accordion.ItemContent px={0} pb={0}><Accordion.ItemBody>
                            <Text fontSize="xs" color="gray.500" mb={2}>
                              These worksites were matched to existing records by name similarity instead of creating duplicates.
                            </Text>
                            <Box maxH="150px" overflowY="auto">
                              <Table.Root size="sm">
                                <Table.Header>
                                  <Table.Row>
                                    <Table.ColumnHeader>CSV Name</Table.ColumnHeader>
                                    <Table.ColumnHeader>Matched To</Table.ColumnHeader>
                                    <Table.ColumnHeader textAlign='end'>Match %</Table.ColumnHeader>
                                    <Table.ColumnHeader textAlign='end'>Rows</Table.ColumnHeader>
                                  </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                  {result.fuzzyMatches.map((fm, i) => (
                                    <Table.Row key={i}>
                                      <Table.Cell fontSize="xs">{fm.csvName}</Table.Cell>
                                      <Table.Cell fontSize="xs" fontWeight="medium">{fm.matchedName}</Table.Cell>
                                      <Table.Cell fontSize="xs" textAlign='end'>{Math.round(fm.similarity * 100)}%</Table.Cell>
                                      <Table.Cell fontSize="xs" textAlign='end'>{fm.affectedRows}</Table.Cell>
                                    </Table.Row>
                                  ))}
                                </Table.Body>
                              </Table.Root>
                            </Box>
                          </Accordion.ItemBody></Accordion.ItemContent>
                      </Accordion.Item>
                    </Accordion.Root>
                  )}

                  {/* Error details */}
                  {result.errors.length > 0 && (
                    <Accordion.Root collapsible mt={3}>
                      <Accordion.Item border="none" value='item-1'>
                        <Accordion.ItemTrigger px={0} py={1}>
                          <Text fontSize="sm" color="orange.600" flex="1" textAlign="left">
                            {result.errorCount} error{result.errorCount !== 1 ? 's' : ''}
                          </Text>
                          <Accordion.ItemIndicator />
                        </Accordion.ItemTrigger>
                        <Accordion.ItemContent px={0} pb={0}><Accordion.ItemBody>
                            <Box maxH="200px" overflowY="auto">
                              <Table.Root size="sm">
                                <Table.Header>
                                  <Table.Row>
                                    <Table.ColumnHeader>Row</Table.ColumnHeader>
                                    <Table.ColumnHeader>Contact</Table.ColumnHeader>
                                    <Table.ColumnHeader>Worksite</Table.ColumnHeader>
                                    <Table.ColumnHeader>Error</Table.ColumnHeader>
                                  </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                  {result.errors.map((err, i) => (
                                    <Table.Row key={i}>
                                      <Table.Cell>{err.rowNumber}</Table.Cell>
                                      <Table.Cell fontSize="xs">{err.contactName || '—'}</Table.Cell>
                                      <Table.Cell fontSize="xs">{err.worksiteName || '—'}</Table.Cell>
                                      <Table.Cell fontSize="xs" color="red.600">{err.message}</Table.Cell>
                                    </Table.Row>
                                  ))}
                                </Table.Body>
                              </Table.Root>
                            </Box>
                          </Accordion.ItemBody></Accordion.ItemContent>
                      </Accordion.Item>
                    </Accordion.Root>
                  )}
                </Box>
              )}
            </VStack>
          </Dialog.Body>
          <Dialog.Footer>
            <Button size="sm" variant="ghost" mr={3} onClick={handleClose}>
              {result ? 'Close' : 'Cancel'}
            </Button>
            {!result && (
              <Button
                size="sm"
                colorPalette="brand"
                onClick={handleImport}
                disabled={!selectedFile}
                loading={importMutation.isPending}
                loadingText="Importing..."><FiUpload />Import
                            </Button>
            )}
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </ResponsiveModal>
  );
}
